// Constants
import {PAYMENT_METHODS, GC_ACTION_ERROR_TYPE} from '../constants'

// Hooks
import {useGiftCardLogic} from './use-gift-card-logic'
import {useGiftCertificate, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useRef} from 'react'

/**
 * Custom hook that provides actions for adding and removing gift card payment instruments
 * to/from a basket.
 *
 * @param {Object} basket - The basket object.
 * @returns {Object} An object containing functions to add and remove gift card payment instruments.
 */
export const useGiftCardActions = (basket) => {
    const {
        checkIfGCAndBasketCurrenciesAreSame,
        checkIfGCExpired,
        checkIfGiftcardIsInPaymentInstruments,
        checkIfThereAnyBalance,
        getGiftcardPaymentInstruments,
        getTotalGiftcardCreditsApplied
    } = useGiftCardLogic()

    const giftCardCode = useRef('')
    // We only trigger this from the methods below, no need to fetch anything here
    const {refetch: refetchGiftCertificate} = useGiftCertificate(
        {body: {giftCertificateCode: giftCardCode.current?.trim()}},
        {enabled: false}
    )

    const {mutateAsync: addPaymentInstrumentToBasket} = useShopperBasketsMutation(
        'addPaymentInstrumentToBasket'
    )
    const {mutateAsync: removePaymentInstrumentFromBasket} = useShopperBasketsMutation(
        'removePaymentInstrumentFromBasket'
    )
    const {mutateAsync: removeGiftCardLineItemFromBasket} = useShopperBasketsMutation(
        'removeGiftCertificateItemFromBasket'
    )

    /**
     * Retrieves the details of a gift card using the provided gift card code.
     *
     * @param {string} gcCode - The gift card code to be used for fetching the gift card details.
     * @returns {Promise<Object>} A promise that resolves with the gift certificate data if successful,
     *                            or rejects with an error type if the gift card does not exist.
     */
    const getGiftCardDetails = (gcCode) => {
        giftCardCode.current = gcCode

        return new Promise((resolve, reject) => {
            // Using timeout as a workaround for useGiftCertificate, which needs to have access to the updated
            // giftCardCode before running refetch()
            setTimeout(async () => {
                const {data: giftCertificate, isError} = await refetchGiftCertificate()

                if (isError) {
                    reject(new Error(GC_ACTION_ERROR_TYPE.GC_NOT_EXIST))
                } else {
                    resolve(giftCertificate)
                }
            }, 0)
        })
    }

    /**
     * @typedef {Object} CheckGiftCardBalanceResp
     * @property {object} data - The gift certificate data.
     * @property {string} error - The error type, if any.
     */

    /**
     *
     * @param {string} gcCode - The gift card code to be used for fetching the gift card details.
     * @returns {CheckGiftCardBalanceResp}
     */
    const checkGiftCardBalance = async (gcCode) => {
        let giftCertificate = null,
            error = null

        try {
            giftCertificate = await getGiftCardDetails(gcCode)
        } catch {
            /* empty */
        }

        // Gift certificate doesn't exist
        if (!giftCertificate) {
            error = GC_ACTION_ERROR_TYPE.GC_NOT_EXIST
        }
        // Gift certificate exists, but has expired
        else if (checkIfGCExpired(giftCertificate)) {
            error = GC_ACTION_ERROR_TYPE.GC_EXPIRED
        }
        // Gift certificate exists and is NOT expired, but has no balance
        else if (!checkIfThereAnyBalance(giftCertificate)) {
            error = GC_ACTION_ERROR_TYPE.GC_NO_BALANCE
        }

        return {data: giftCertificate, error}
    }

    /**
     * Adds a gift card as a payment instrument to the given basket.
     *
     * This function attempts to add a gift card to the basket by first verifying its validity and balance.
     * It checks for various conditions such as existence, expiration, balance, and currency match before
     * proceeding to add the gift card as a payment instrument.
     *
     * @param {string} gcCode - The code of the gift card to be added.
     * @returns {Promise<Object>} A promise that resolves with the updated basket object if the gift card is
     *                            successfully added, or rejects with an error type indicating the reason for failure.
     */
    const addGiftCardPaymentInstrument = async (gcCode) => {
        let giftCertificate = null

        try {
            giftCertificate = await getGiftCardDetails(gcCode)
        } catch {
            /* empty */
        }

        // START Validation
        let errorType = null,
            isValid = true

        // Gift certificate doesn't exist
        if (!giftCertificate) {
            errorType = GC_ACTION_ERROR_TYPE.GC_NOT_EXIST
            isValid = false
        }
        // Gift certificate is already added to this basket
        else if (checkIfGiftcardIsInPaymentInstruments(basket, giftCertificate)) {
            errorType = GC_ACTION_ERROR_TYPE.GC_ALREADY_IN_USE
            isValid = false
        }
        // Gift certificate exists, but has expired
        else if (checkIfGCExpired(giftCertificate)) {
            errorType = GC_ACTION_ERROR_TYPE.GC_EXPIRED
            isValid = false
        }
        // Gift certificate exists and is NOT expired, but has no balance
        else if (!checkIfThereAnyBalance(giftCertificate)) {
            errorType = GC_ACTION_ERROR_TYPE.GC_NO_BALANCE
            isValid = false
        }
        // Gift certificate exists and is NOT expired, but the currencies don't match
        else if (!checkIfGCAndBasketCurrenciesAreSame(basket, giftCertificate)) {
            errorType = GC_ACTION_ERROR_TYPE.GC_CURRENCY_NOT_MATCH
            isValid = false
        }
        // END Validation

        if (isValid) {
            const cartTotal = basket?.orderTotal || basket?.productTotal
            const amountOwed = cartTotal - getTotalGiftcardCreditsApplied(basket)

            const amountToUse = Math.min(giftCertificate.balance, amountOwed)
            const paymentInstrument = {
                giftCertificateCode: gcCode.trim(),
                paymentMethodId: PAYMENT_METHODS.GIFT_CERTIFICATE,
                amount: parseFloat(amountToUse)
            }

            try {
                const updatedBasket = await addPaymentInstrumentToBasket({
                    parameters: {
                        basketId: basket?.basketId
                    },
                    body: paymentInstrument
                })
                return updatedBasket
            } catch {
                throw new Error(GC_ACTION_ERROR_TYPE.API_ERROR)
            }
        } else {
            throw new Error(errorType)
        }
    }

    /**
     * Removes a gift card payment instrument from the given basket.
     *
     * @param {string} paymentInstrumentId - The unique identifier of the payment instrument to be removed.
     * @returns {Promise<Object>} A promise that resolves to the updated basket object or rejects with an API error type.
     */
    const removeGiftCardPaymentInstrument = async (paymentInstrumentId) => {
        try {
            const updatedBasket = await removePaymentInstrumentFromBasket({
                parameters: {
                    basketId: basket.basketId,
                    paymentInstrumentId
                }
            })
            return updatedBasket
        } catch {
            throw new Error(GC_ACTION_ERROR_TYPE.API_ERROR)
        }
    }

    /**
     * Removes all gift card payment instruments from the given basket.
     *
     * @returns {Promise<Object>} A promise that resolves with the updated basket object
     *                            after all gift card payment instruments have been removed,
     *                            or rejects with an error type if the operation fails.
     */
    const removeAllGiftCardPaymentInstruments = async () => {
        let updatedBasket = basket

        try {
            // Remove one by one to avoid issues on the BE
            let GCPIsToRemove = getGiftcardPaymentInstruments(basket)
            while (GCPIsToRemove.length > 0) {
                const currentGC = GCPIsToRemove.pop()
                updatedBasket = await removePaymentInstrumentFromBasket({
                    parameters: {
                        basketId: basket.basketId,
                        paymentInstrumentId: currentGC.paymentInstrumentId
                    }
                })
            }
            return updatedBasket
        } catch {
            throw new Error(GC_ACTION_ERROR_TYPE.API_ERROR)
        }
    }

    /**
     * Asynchronously removes a gift card line item from the current basket.
     *
     * @param {string} giftCertificateItemId - The unique identifier of the gift certificate line item to be removed.
     * @param {boolean} [isCartPage=false] - Indicates if the removal is being performed from the cart page.
     * @returns {Promise<Object>} - A promise that resolves to the updated basket object after the gift card line item is removed.
     * @throws {Error} Throws an error of type GC_ACTION_ERROR_TYPE.API_ERROR if the removal operation fails.
     */
    const removeGiftCardLineItem = async (giftCertificateItemId, isCartPage = false) => {
        try {
            const updatedBasket = await removeGiftCardLineItemFromBasket({
                parameters: {
                    basketId: basket.basketId,
                    giftCertificateItemId,
                    c_iscartpage: isCartPage ? 'true' : 'false'
                }
            })
            return updatedBasket
        } catch {
            throw new Error(GC_ACTION_ERROR_TYPE.API_ERROR)
        }
    }

    /**
     * Asynchronously removes all gift card line items from the current basket, one at a time.
     * This sequential removal is necessary to avoid API inconsistencies that may occur when removing all items simultaneously.
     *
     * @param {boolean} [isCartPage=false] - Indicates if the operation is being performed on the cart page.
     * @returns {Promise<Object>} - A promise that resolves to the updated basket object after all gift card line items have been removed.
     */
    const removeAllGiftCardLineItems = async (isCartPage = false) => {
        let updatedBasket = basket
        let gcLineItems = updatedBasket?.giftCertificateItems ?? []

        // Removing them one by one and not all at once,
        // because the API starts responding with wrong data if we remove them all at once.
        while (gcLineItems.length) {
            updatedBasket = await removeGiftCardLineItem(
                gcLineItems[0].giftCertificateItemId,
                isCartPage
            )
            gcLineItems = updatedBasket.giftCertificateItems ?? []
        }

        return updatedBasket
    }

    return {
        getGiftCardDetails,
        checkGiftCardBalance,
        addGiftCardPaymentInstrument,
        removeGiftCardPaymentInstrument,
        removeAllGiftCardPaymentInstruments,
        removeGiftCardLineItem,
        removeAllGiftCardLineItems
    }
}
